import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faker } from "@faker-js/faker";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import React, { useState } from "react";

import { Input, Paragraph, Small } from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import {
  ApiTreatmentPlanTemplateFieldFieldTypeChoices,
  ApiTreatmentPlanTemplateFieldTypeChoices,
} from "@api/graphql/types-and-hooks";
import {
  DomainState,
  TemplateFieldState,
} from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditor";
import { TreatmentPlanTemplateEditorAddButton } from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditorAddButton";
import { TreatmentPlanTemplateEditorField } from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditorField";

interface TreatmentPlanTemplateEditorDomainProps {
  domain: DomainState;
  onUpdateDomain: (domain: DomainState) => void;
  onRemoveDomain: () => void;
  readonly: boolean;
}

export const TreatmentPlanTemplateEditorDomain: React.FC<TreatmentPlanTemplateEditorDomainProps> = ({
  domain,
  onUpdateDomain,
  onRemoveDomain,
  readonly,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: domain.id,
    disabled: readonly,
  });
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState(domain.name);
  const [localDescription, setLocalDescription] = useState(domain.description ?? "");
  // Tracks the last domain.description we synced from, so a prop change (e.g. after
  // save/reload) can be adjusted during render instead of via a useEffect.
  const [syncedDescription, setSyncedDescription] = useState(domain.description);
  if (domain.description !== syncedDescription) {
    setSyncedDescription(domain.description);
    setLocalDescription(domain.description ?? "");
  }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : undefined,
  };

  const fieldSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const actionMenuItems: PopoverMenuItem[] = [
    {
      label: "Delete Domain",
      Icon: TrashIcon,
      callback: onRemoveDomain,
    },
  ];

  const makeFieldDragEndHandler =
    (fields: TemplateFieldState[], onUpdate: (fields: TemplateFieldState[]) => void) => (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = fields.findIndex((f) => f.id === active.id);
        const newIndex = fields.findIndex((f) => f.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(fields, oldIndex, newIndex).map((f, i) => ({
            ...f,
            rankOrder: i + 1,
          }));
          onUpdate(reordered);
        }
      }
    };

  const goalFields = domain.templateFields.filter((f) => f.type === ApiTreatmentPlanTemplateFieldTypeChoices.Goal);
  const targetFields = domain.templateFields.filter((f) => f.type === ApiTreatmentPlanTemplateFieldTypeChoices.Target);

  const handleUpdateGoalFields = (updated: TemplateFieldState[]) => {
    onUpdateDomain({ ...domain, templateFields: [...updated, ...targetFields] });
  };

  const handleUpdateTargetFields = (updated: TemplateFieldState[]) => {
    onUpdateDomain({ ...domain, templateFields: [...goalFields, ...updated] });
  };

  const handleAddField = (fieldType: string) => {
    const fieldsOfType = domain.templateFields.filter((f) => f.type === fieldType);
    const newField: TemplateFieldState = {
      id: `temp-${faker.string.uuid()}`,
      fieldIdentifier: "",
      title: "",
      fieldType: ApiTreatmentPlanTemplateFieldFieldTypeChoices.SimpleText,
      rankOrder: fieldsOfType.length + 1,
      isRequired: false,
      isEditable: true,
      isEnabled: true,
      type: fieldType,
      reportGoalField: false,
      reportTargetField: false,
    };
    onUpdateDomain({ ...domain, templateFields: [...domain.templateFields, newField] });
  };

  return (
    <div ref={setNodeRef} style={style} className="border border-limestone-200 rounded-lg bg-white">
      {/* Domain Header */}
      <div className="flex items-center gap-2 px-3 py-3 group/domain">
        {!readonly && (
          <div {...attributes} {...listeners} className="text-text-secondary cursor-grab shrink-0">
            <div className="flex w-5">
              <EllipsisVerticalIcon className="h-5 w-5" />
              <EllipsisVerticalIcon className="h-5 w-5 -ml-3" />
            </div>
          </div>
        )}
        <button type="button" onClick={() => setIsExpanded(!isExpanded)} className="shrink-0">
          {isExpanded ? (
            <ChevronUpIcon className="h-4 w-4 text-text-secondary" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-text-secondary" />
          )}
        </button>
        <div className="flex items-center flex-1 min-w-0 group/name">
          {isEditingName ? (
            <Input
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              autoFocus={true}
              onBlur={() => {
                const trimmedName = editingName.trim();
                setIsEditingName(false);
                if (!readonly && trimmedName && trimmedName !== domain.name) {
                  onUpdateDomain({ ...domain, name: trimmedName });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className="[&>div>input]:ring-0 [&>div>input]:shadow-none [&>div>input]:bg-transparent -mb-6 font-semibold"
              placeholder="Domain name"
              name={`domain-${domain.id}`}
              label=""
            />
          ) : (
            <>
              <Paragraph
                displayType="loud"
                colorType="primary"
                className="cursor-pointer"
                onClick={(e) => {
                  if (readonly) return;
                  e.stopPropagation();
                  setEditingName(domain.name);
                  setIsEditingName(true);
                }}
              >
                {domain.name || "Untitled Domain"}
              </Paragraph>
              {!readonly && (
                <PencilSquareIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingName(domain.name);
                    setIsEditingName(true);
                  }}
                  className="h-6 w-6 ml-2 cursor-pointer rounded-2xl bg-limestone-50 hover:bg-limestone-100 p-1 text-text-secondary opacity-0 group-hover/name:opacity-100"
                />
              )}
            </>
          )}
        </div>
        {!readonly && (
          <div className="opacity-0 group-hover/domain:opacity-100 duration-200 transition-opacity shrink-0">
            <PopoverMenu openDirection="to-right" menuItems={actionMenuItems} />
          </div>
        )}
      </div>

      <div className="px-3 pb-3">
        <Input
          value={localDescription}
          onChange={(e) => setLocalDescription(e.target.value)}
          onBlur={() => {
            const trimmedDescription = localDescription.trim();
            if (!readonly && trimmedDescription !== (domain.description ?? "")) {
              onUpdateDomain({ ...domain, description: trimmedDescription || undefined });
            }
          }}
          placeholder="Optional guidance shown to clinicians while writing goals in this domain"
          name={`domain-description-${domain.id}`}
          label="Description"
          disabled={readonly}
        />
      </div>

      {/* Domain Fields — split by Goal / Target */}
      {isExpanded && (
        <div className="px-4 pb-3">
          <Small displayType="loud" colorType="secondary" className="mb-2 block">
            Goal
          </Small>
          <DndContext
            sensors={fieldSensors}
            collisionDetection={closestCenter}
            onDragEnd={makeFieldDragEndHandler(goalFields, handleUpdateGoalFields)}
          >
            <SortableContext items={goalFields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {goalFields.map((field) => (
                  <TreatmentPlanTemplateEditorField
                    key={field.id}
                    field={field}
                    onUpdate={(updated) =>
                      handleUpdateGoalFields(goalFields.map((f) => (f.id === updated.id ? updated : f)))
                    }
                    onRemove={() =>
                      handleUpdateGoalFields(
                        goalFields.filter((f) => f.id !== field.id).map((f, i) => ({ ...f, rankOrder: i + 1 })),
                      )
                    }
                    readonly={readonly || !field.isEditable}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          {!readonly && (
            <TreatmentPlanTemplateEditorAddButton
              label="Add Domain Goal Field"
              onClick={() => handleAddField(ApiTreatmentPlanTemplateFieldTypeChoices.Goal)}
            />
          )}

          <Small displayType="loud" colorType="secondary" className="mb-2 block mt-6">
            Target
          </Small>
          <DndContext
            sensors={fieldSensors}
            collisionDetection={closestCenter}
            onDragEnd={makeFieldDragEndHandler(targetFields, handleUpdateTargetFields)}
          >
            <SortableContext items={targetFields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {targetFields.map((field) => (
                  <TreatmentPlanTemplateEditorField
                    key={field.id}
                    field={field}
                    onUpdate={(updated) =>
                      handleUpdateTargetFields(targetFields.map((f) => (f.id === updated.id ? updated : f)))
                    }
                    onRemove={() =>
                      handleUpdateTargetFields(
                        targetFields.filter((f) => f.id !== field.id).map((f, i) => ({ ...f, rankOrder: i + 1 })),
                      )
                    }
                    readonly={readonly || !field.isEditable}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          {!readonly && (
            <TreatmentPlanTemplateEditorAddButton
              label="Add Domain Target Field"
              onClick={() => handleAddField(ApiTreatmentPlanTemplateFieldTypeChoices.Target)}
            />
          )}
        </div>
      )}
    </div>
  );
};
